import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Sparkles, Eye, EyeOff } from "lucide-react";
import taskeerLogo from "@/assets/taskeerlogo.png";
import { useAuth } from "@/hooks/useAuth";
import { z } from "zod";
import { trackSignup, trackLogin } from "@/lib/gtag";
import { CountryCodeSelect } from "@/components/CountryCodeSelect";
import { CountrySelect } from "@/components/CountrySelect";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSelector from "@/components/landing/LanguageSelector";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Validation schemas
const loginSchema = z.object({
  email: z.string().trim().email("Email inválido").max(255, "Email muito longo"),
  password: z.string().min(6, "Senha deve ter no mínimo 6 caracteres"),
});

const signupSchema = z.object({
  name: z.string().trim().min(2, "Nome muito curto").max(100, "Nome muito longo"),
  email: z.string().trim().email("Email inválido").max(255, "Email muito longo"),
  phone: z.string().trim().min(8, "Telefone muito curto").max(20, "Telefone muito longo"),
  password: z.string().min(6, "Senha deve ter no mínimo 6 caracteres"),
  confirmPassword: z.string().min(6, "Confirme sua senha"),
  birthDate: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/, "Data inválida. Use o formato dd/mm/yyyy"),
  profession: z.string().min(1, "Selecione sua profissão"),
  platformInterest: z.string().min(1, "Selecione o que procura na plataforma"),
}).refine((data) => data.password === data.confirmPassword, {
  message: "As senhas não coincidem",
  path: ["confirmPassword"],
});

const Auth = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("login");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [resetCooldownSeconds, setResetCooldownSeconds] = useState(0);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  // Friendly message when an email link is invalid/expired
  useEffect(() => {
    const hash = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : window.location.hash;

    if (!hash) return;

    const params = new URLSearchParams(hash);
    const errorCode = params.get("error_code");

    if (!errorCode) return;

    toast({
      title: t("auth.invalidResetLink"),
      description: t("auth.invalidResetLinkDesc"),
      variant: "destructive",
    });

    // Clear hash so it doesn't keep showing on refresh
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${window.location.search}`
    );
  }, [toast, t]);

  // Simple cooldown to avoid hitting the backend email rate limit (429)
  useEffect(() => {
    if (resetCooldownSeconds <= 0) return;

    const tmr = window.setInterval(() => {
      setResetCooldownSeconds((s) => Math.max(0, s - 1));
    }, 1000);

    return () => window.clearInterval(tmr);
  }, [resetCooldownSeconds]);

  // Login state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Signup state
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupConfirmPassword, setSignupConfirmPassword] = useState("");
  const [signupName, setSignupName] = useState("");
  const [signupPhone, setSignupPhone] = useState("");
  const [signupCountryCode, setSignupCountryCode] = useState("+1");
  const [signupBirthDate, setSignupBirthDate] = useState("");
  const [signupCountry, setSignupCountry] = useState("US");
  const [signupProfession, setSignupProfession] = useState("");
  const [signupPlatformInterest, setSignupPlatformInterest] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const getPublicAppUrl = () => {
    // Use the current origin so the backend uses the same domain the user is on.
    // (Avoid hardcoded URLs, since system-managed URLs can change.)
    return window.location.origin;
  };

  const sendPasswordReset = async (rawEmail: string) => {
    if (resetCooldownSeconds > 0) return false;

    const validation = z.string().trim().email("Email inválido").safeParse(rawEmail);

    if (!validation.success) {
      toast({
        title: t("auth.validationError"),
        description: validation.error.issues[0].message,
        variant: "destructive",
      });
      return false;
    }

    setLoading(true);

    try {
      const publicUrl = getPublicAppUrl();
      const { error } = await supabase.auth.resetPasswordForEmail(validation.data, {
        redirectTo: `${publicUrl}/reset-password`,
      });

      if (error) throw error;

      setResetCooldownSeconds(30);

      toast({
        title: t("auth.resetEmailSent"),
        description: t("auth.resetEmailSentDesc"),
      });

      return true;
    } catch (error: any) {
      const message = String(error?.message ?? "");

      // Example: "429: For security purposes, you can only request this after 22 seconds."
      const match = message.match(/after\s+(\d+)\s+seconds/i);
      if (match?.[1]) {
        const seconds = Number(match[1]);
        if (Number.isFinite(seconds) && seconds > 0) {
          setResetCooldownSeconds(seconds);
        }
      }

      toast({
        title: t("auth.resetError"),
        description: message,
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    const ok = await sendPasswordReset(forgotPasswordEmail);
    if (ok) {
      setShowForgotPassword(false);
      setForgotPasswordEmail("");
    }
  };

  const handleForgotPasswordFromLogin = () => {
    setForgotPasswordEmail(loginEmail.trim());
    setShowForgotPassword(true);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate input
    const validation = loginSchema.safeParse({
      email: loginEmail, 
      password: loginPassword 
    });
    
    if (!validation.success) {
      toast({
        title: t('auth.validationError'),
        description: validation.error.issues[0].message,
        variant: "destructive",
      });
      return;
    }
    
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: validation.data.email,
        password: validation.data.password,
      });

      if (error) throw error;

      trackLogin();

      toast({
        title: t('auth.loginSuccess'),
        description: t('auth.loginSuccessDesc'),
      });

      navigate("/dashboard");
    } catch (error: any) {
      toast({
        title: t('auth.loginError'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate input
    const validation = signupSchema.safeParse({
      name: signupName,
      email: signupEmail,
      phone: signupPhone,
      password: signupPassword,
      confirmPassword: signupConfirmPassword,
      birthDate: signupBirthDate,
      profession: signupProfession,
      platformInterest: signupPlatformInterest,
    });
    
    if (!validation.success) {
      toast({
        title: t('auth.validationError'),
        description: validation.error.issues[0].message,
        variant: "destructive",
      });
      return;
    }
    
    setLoading(true);

    try {
      // Convert dd/mm/yyyy to yyyy-mm-dd
      const [day, month, year] = validation.data.birthDate.split('/');
      const formattedBirthDate = `${year}-${month}-${day}`;
      const publicUrl = getPublicAppUrl();

      // Format phone number: remove all non-numeric characters and combine with country code
      const cleanPhone = validation.data.phone.replace(/\D/g, '');
      const cleanCountryCode = signupCountryCode.replace(/\D/g, '');
      const formattedPhone = `+${cleanCountryCode}${cleanPhone}`;

      const { data, error } = await supabase.auth.signUp({
        email: validation.data.email,
        password: validation.data.password,
        options: {
          emailRedirectTo: `${publicUrl}/dashboard`,
          data: {
            name: validation.data.name,
            phone: formattedPhone,
            birth_date: formattedBirthDate,
            country: signupCountry,
            profession: validation.data.profession,
            platform_interest: validation.data.platformInterest,
          },
        },
      });

      if (error) throw error;

      trackSignup();

      toast({
        title: t('auth.signupSuccess'),
        description: t('auth.signupSuccessDesc'),
        className: "bg-blue-600 text-white border-blue-600",
      });

      // Switch to login tab
      setLoginEmail(validation.data.email);
      setActiveTab("login");
    } catch (error: any) {
      toast({
        title: t('auth.signupError'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute top-4 right-4 z-20">
        <LanguageSelector />
      </div>
      <div className="w-full max-w-md space-y-8 animate-fade-in relative z-10">
        {/* Logo */}
        <div className="text-center">
          <img src={taskeerLogo} alt="Taskeer" className="h-12 mx-auto mb-2" />
          <p className="text-muted-foreground">
            {t('auth.subtitle')}
          </p>
        </div>

        {/* Auth Card */}
        <Card className="p-6 shadow-xl backdrop-blur-xl bg-card/90 border-border/50">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login">{t('auth.login')}</TabsTrigger>
              <TabsTrigger value="signup">{t('auth.signup')}</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              {showForgotPassword ? (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-semibold">{t('auth.forgotPassword')}</h3>
                    <p className="text-sm text-muted-foreground">{t('auth.forgotPasswordDesc')}</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">{t('auth.email')}</Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      required
                      className="bg-background"
                    />
                  </div>


                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading || resetCooldownSeconds > 0}
                  >
                    {loading
                      ? t('auth.sending')
                      : resetCooldownSeconds > 0
                        ? `${t('auth.sendResetLink')} (${resetCooldownSeconds}s)`
                        : t('auth.sendResetLink')}
                  </Button>
                  
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={() => setShowForgotPassword(false)}
                  >
                    {t('auth.backToLogin')}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">{t('auth.email')}</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      required
                      className="bg-background"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="login-password">{t('auth.password')}</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      required
                      className="bg-background"
                    />
                  </div>

                  <div className="text-right">
                    <button
                      type="button"
                      onClick={handleForgotPasswordFromLogin}
                      className="text-sm text-primary hover:underline"
                    >
                      {t('auth.forgotPassword')}
                    </button>
                  </div>


                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading}
                  >
                    {loading ? t('auth.loggingIn') : t('auth.login')}
                  </Button>
                </form>
              )}
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleSignup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-country">{t('auth.country')} *</Label>
                  <CountrySelect
                    value={signupCountry}
                    onChange={setSignupCountry}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-name">{t('auth.fullName')} *</Label>
                  <Input
                    id="signup-name"
                    type="text"
                    placeholder="Your full name"
                    value={signupName}
                    onChange={(e) => setSignupName(e.target.value)}
                    required
                    className="bg-background"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-email">{t('auth.email')} *</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="your@email.com"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    required
                    className="bg-background"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-phone">{t('auth.phone')} *</Label>
                  <div className="flex gap-2">
                    <CountryCodeSelect
                      value={signupCountryCode}
                      onChange={setSignupCountryCode}
                    />
                    <Input
                      id="signup-phone"
                      type="tel"
                      placeholder="(11) 99999-9999"
                      value={signupPhone}
                      onChange={(e) => setSignupPhone(e.target.value)}
                      required
                      className="bg-background flex-1"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-birthdate">{t('auth.birthDate')} *</Label>
                  <Input
                    id="signup-birthdate"
                    type="text"
                    placeholder="dd/mm/yyyy"
                    value={signupBirthDate}
                    onChange={(e) => {
                      const input = e.target.value.replace(/\D/g, '');
                      if (input.length <= 8) {
                        let formatted = input;
                        if (input.length >= 3) {
                          formatted = input.slice(0, 2) + '/' + input.slice(2);
                        }
                        if (input.length >= 5) {
                          formatted = input.slice(0, 2) + '/' + input.slice(2, 4) + '/' + input.slice(4);
                        }
                        setSignupBirthDate(formatted);
                      }
                    }}
                    required
                    className="bg-background"
                    maxLength={10}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-profession">{t('auth.profession')} *</Label>
                  <Select value={signupProfession} onValueChange={setSignupProfession}>
                    <SelectTrigger className="bg-background">
                      <SelectValue placeholder={t('auth.selectProfession')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="freelancer">{t('auth.professions.freelancer')}</SelectItem>
                      <SelectItem value="autonomo">{t('auth.professions.autonomo')}</SelectItem>
                      <SelectItem value="agencia">{t('auth.professions.agencia')}</SelectItem>
                      <SelectItem value="empresa">{t('auth.professions.empresa')}</SelectItem>
                      <SelectItem value="estudante">{t('auth.professions.estudante')}</SelectItem>
                      <SelectItem value="outro">{t('auth.professions.outro')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-interest">{t('auth.platformInterest')} *</Label>
                  <Select value={signupPlatformInterest} onValueChange={setSignupPlatformInterest}>
                    <SelectTrigger className="bg-background">
                      <SelectValue placeholder={t('auth.selectInterest')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gerenciar_projetos">{t('auth.interests.gerenciarProjetos')}</SelectItem>
                      <SelectItem value="cobrar_clientes">{t('auth.interests.cobrarClientes')}</SelectItem>
                      <SelectItem value="organizar_tarefas">{t('auth.interests.organizarTarefas')}</SelectItem>
                      <SelectItem value="colaborar_equipe">{t('auth.interests.colaborarEquipe')}</SelectItem>
                      <SelectItem value="controle_financeiro">{t('auth.interests.controleFinanceiro')}</SelectItem>
                      <SelectItem value="tudo">{t('auth.interests.tudo')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-password">{t('auth.password')} *</Label>
                  <div className="relative">
                    <Input
                      id="signup-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={signupPassword}
                      onChange={(e) => setSignupPassword(e.target.value)}
                      required
                      minLength={6}
                      className="bg-background pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-confirm-password">{t('auth.confirmPassword')} *</Label>
                  <div className="relative">
                    <Input
                      id="signup-confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={signupConfirmPassword}
                      onChange={(e) => setSignupConfirmPassword(e.target.value)}
                      required
                      minLength={6}
                      className="bg-background pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>


                <Button
                  type="submit"
                  className="w-full prime-gradient shadow-prime"
                  disabled={loading}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {loading ? t('auth.creatingAccount') : t('auth.signup')}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </Card>

        {/* Back to Home */}
        <div className="text-center">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="text-muted-foreground hover:text-foreground"
          >
            {t('auth.backToHome')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Auth;
